import { Capacitor } from '@capacitor/core';
import { useCallback, useState } from 'react';
import type { V2Evidence } from '../../domain/evidence';
import { useT } from '../../i18n/i18n';
import { captureFromCameraAction, importFromLibraryAction, type EvidenceTarget } from '../../state/captureFlow';

// The two capture entries of every photo surface (Authority §8.2: Take Photo · Choose Library),
// bound to ONE evidence target. The lanes themselves live in src/state/captureFlow.ts and are not
// changed here: this hook only owns the busy state and the album-outcome message every camera
// photo prints (never hidden — OC §6.1). Shared by the area strip, the scan panel, items and findings.
export interface CaptureMessage {
  kind: 'ok' | 'err' | 'info';
  text: string;
}

export interface EvidenceCapture {
  busy: 'camera' | 'library' | null;
  message: CaptureMessage | null;
  clearMessage: () => void;
  takePhoto: () => Promise<V2Evidence | null>;
  chooseLibrary: () => Promise<V2Evidence[]>;
}

export function useEvidenceCapture(target: EvidenceTarget, onCaptured?: (evidence: V2Evidence[]) => void): EvidenceCapture {
  const { t } = useT();
  const [busy, setBusy] = useState<'camera' | 'library' | null>(null);
  const [message, setMessage] = useState<CaptureMessage | null>(null);
  const { inspectionId, areaId, itemId, findingId } = target;

  const takePhoto = useCallback(async (): Promise<V2Evidence | null> => {
    setBusy('camera');
    setMessage(null);
    try {
      const outcome = await captureFromCameraAction({ inspectionId, areaId, itemId, findingId });
      if (outcome.kind === 'error') {
        setMessage({ kind: 'err', text: outcome.message });
        return null;
      }
      if (outcome.kind === 'cancelled') return null;
      onCaptured?.([outcome.evidence]);
      if (outcome.evidence.albumSaveState === 'failed') {
        setMessage({ kind: 'err', text: t('photo.albumFailedDetail', { detail: outcome.evidence.albumSaveDetail }) });
      } else if (outcome.evidence.albumSaveState === 'unavailable') {
        // The web version (a computer's file picker): there is no album, and the original stays where it was.
        setMessage({ kind: 'info', text: Capacitor.isNativePlatform() ? t('photo.albumUnavailableDetail', { detail: outcome.evidence.albumSaveDetail }) : t('photo.webNoAlbum') });
      }
      return outcome.evidence;
    } finally {
      setBusy(null);
    }
  }, [inspectionId, areaId, itemId, findingId, onCaptured, t]);

  const chooseLibrary = useCallback(async (): Promise<V2Evidence[]> => {
    setBusy('library');
    setMessage(null);
    try {
      const outcome = await importFromLibraryAction({ inspectionId, areaId, itemId, findingId });
      if (outcome.kind === 'error') {
        setMessage({ kind: 'err', text: outcome.message });
        return [];
      }
      if (outcome.kind === 'cancelled') return [];
      onCaptured?.(outcome.evidence);
      return outcome.evidence;
    } finally {
      setBusy(null);
    }
  }, [inspectionId, areaId, itemId, findingId, onCaptured]);

  const clearMessage = useCallback(() => setMessage(null), []);

  return { busy, message, clearMessage, takePhoto, chooseLibrary };
}
